import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import storeLocatorQuery from '../StoreLocator/StoreLocator.gql';
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import { LocationStateContext } from '../StoreLocator/StoreLocator';
import LoadingPage from '../LoadingPage';
import { styled } from 'linaria/react';
import { theme } from '../Theme';

const MapWrapper = styled('div')`
  height: 40vh;
  width: 100%;
  max-width: 80rem;
  padding: 0 32px;
  margin: auto;
  position: relative;
  overflow: hidden;

  ${theme.below.md} {
    height: 50vh;
    padding: 0 16px;
  }

  .map-container {
    height: 80%;
    width: 100%;

    .gm-style-iw {
      background-color: white !important;
    }

    .gm-style-iw-a {
      width: 300px !important;
    }

    ${theme.below.md} {
      height: 80%;
    }
  }
`;

const LoadingElement = () => <div style={{ height: '100%' }}></div>;

export const Map = ({ title, storeID }) => {
  const { data = {}, loading = true } = useQuery(storeLocatorQuery);
  const [specificStore, setSpecificStore] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const mapRef = useRef(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Create a minimal location state
  const locationState = {
    activeLocation: null,
    expandedLocation: null,
    setHighlightedLocation: () => {},
    toggleExpandedLocation: () => {},
  };

  // Find the specific store by ID
  useEffect(() => {
    if (data?.stores && data.stores.length > 0) {
      const foundStore = data.stores.find(
        (store) => parseInt(store.id) === parseInt(storeID?.value || storeID)
      );

      if (foundStore) {
        setSpecificStore(foundStore);
      } else {
      }
    }
  }, [data, storeID]);

  if (loading) return <LoadingPage />;

  // Default coordinates in Sweden
  const defaultLat = 59.247948;
  const defaultLng = 14.755806;

  // Get coordinates from the specific store if available
  const latitude = specificStore?.coordinates?.latitude
    ? parseFloat(specificStore.coordinates.latitude)
    : defaultLat;

  const longitude = specificStore?.coordinates?.longitude
    ? parseFloat(specificStore.coordinates.longitude)
    : defaultLng;

  // Explicitly create a center object for the map
  const center = {
    lat: latitude,
    lng: longitude,
  };

  return (
    <MapWrapper>
      {title?.value && <h2>{title.value}</h2>}

      <LocationStateContext.Provider value={locationState}>
        <LoadScript
          loadingElement={<LoadingElement />}
          preventGoogleFontsLoading={true}
          googleMapsApiKey={googleMapsApiKey}
        >
          <GoogleMap
            mapContainerClassName="map-container"
            center={center}
            zoom={14}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
            }}
          >
            {specificStore && (
              <>
                <Marker
                  position={center}
                  onClick={() => setShowInfoWindow(true)}
                />
                {showInfoWindow && (
                  <InfoWindow
                    position={center}
                    onCloseClick={() => setShowInfoWindow(false)}
                  >
                    <div style={{ padding: '10px', maxWidth: '200px' }}>
                      <h3>{specificStore.name}</h3>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: specificStore.address1,
                        }}
                      />
                    </div>
                  </InfoWindow>
                )}
              </>
            )}
          </GoogleMap>
        </LoadScript>
      </LocationStateContext.Provider>
    </MapWrapper>
  );
};

export default Map;
